import { SelectProps, FormControl, InputAdornment, Stack, FormHelperText } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import CheckIcon from '@mui/icons-material/Check'
import { FC, memo, ReactNode, useMemo, useState, useRef } from 'react'
import theme from 'theme'
import { escapeRegExp } from 'lib/utils'
import { StyledInputLabel, StyledListSubheader, StyledMenuItem, StyledSelect, StyledTextField } from './selectV2.styles'
import SearchIcon from '@mui/icons-material/Search'
import { RadioCheckedIcon, RadioUncheckedIcon } from 'ui/components/svg'
import { DEFAULT_MENU_PROPS } from './selectV2.configs'
import HoveredPopover from '../../hovered-popover'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import MonthRangePickerV2 from '../month-range-picker-v2'

export type SelectPropsV2 = SelectProps & {
  value?: any
  label: string
  searchable?: boolean
  emptyMessage?: string
  /* An option with `groupHeader: true` renders as a non-selectable heading for the options after it. */
  options: any[]
  optionLabel?: string
  optionValue?: string
  error?: boolean
  helperText?: any
  isAllowedCustomMonthPicker?: boolean
  /* Text shown in the closed field while nothing is selected. MUI skips
   * renderValue on an empty value, so passing this also turns on displayEmpty.
   * Ignored when label is set — an unshrunk label occupies the same slot. */
  placeholder?: string
  /* 'radio' marks the selected option with a radio glyph instead of a trailing
   * check, for lists where the single choice should read as one. */
  optionControl?: 'check' | 'radio'
  /* A footer row that acts on the selection, e.g. "Reset filter". Rendered as a
   * subheader so the Select does not treat it as an option. */
  listAction?: { label: string; onClick: () => void }
  /* Caps how many options the menu renders at once, for lists big enough to make
   * it slow to open. The rest stay reachable through the search field. */
  maxVisibleOptions?: number
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]

const formatDateRangeForDisplay = (dateRangeValue: string): string => {
  const dateRangePattern = /^(\d{4})-(\d{2})-(\d{2}) to (\d{4})-(\d{2})-(\d{2})$/
  const match = dateRangeValue.match(dateRangePattern)

  if (match) {
    const [, startYear, startMonth, , endYear, endMonth] = match
    const startFormatted = `${MONTH_NAMES[parseInt(startMonth, 10) - 1]} ${startYear}`
    const endFormatted = `${MONTH_NAMES[parseInt(endMonth, 10) - 1]} ${endYear}`

    if (startFormatted === endFormatted) {
      return startFormatted
    }

    return `${startFormatted} - ${endFormatted}`
  }

  return dateRangeValue
}

const NO_SEARCH_RESULTS_MESSAGE = 'No results found'

const CUSTOM_MONTH_FORMAT_REGEX = /^\d{4}-\d{2}$/
const DATE_RANGE_FORMAT_REGEX = /^(\d{4})-(\d{2})-(\d{2}) to (\d{4})-(\d{2})-(\d{2})$/

const SelectV2: FC<SelectPropsV2> = ({
  id,
  label,
  value,
  options = [],
  searchable = false,
  emptyMessage = 'No options found',
  MenuProps = DEFAULT_MENU_PROPS,
  optionLabel = 'label',
  optionValue = 'value',
  error,
  helperText = 'Error',
  isAllowedCustomMonthPicker = false,
  placeholder,
  optionControl = 'check',
  listAction,
  maxVisibleOptions,
  ...props
}) => {
  const [searchText, setSearchText] = useState('')
  const [hoveredOption, setHoveredOption] = useState<string | null>(null)
  const selectRef = useRef<any>(null)

  const removeHoverState = () => {
    setHoveredOption(null)
  }

  // Add custom month to options if it's a custom month value
  const optionsWithCustomMonth = useMemo(() => {
    const isExistingOption = options.find((opt) => opt[optionValue] === value)
    if (isAllowedCustomMonthPicker && value && !isExistingOption) {
      const isCustom = CUSTOM_MONTH_FORMAT_REGEX.test(value) || DATE_RANGE_FORMAT_REGEX.test(value)
      if (isCustom) {
        const displayValue = formatDateRangeForDisplay(value)
        return [...options, { [optionLabel]: displayValue, [optionValue]: value }]
      }
    }
    return options
  }, [options, value, isAllowedCustomMonthPicker, optionLabel, optionValue])

  const filteredOptions = useMemo(() => {
    return optionsWithCustomMonth.filter((opt) => {
      if (opt.groupHeader) return true
      const label = String(opt[optionLabel] || '')
      const optVal = opt[optionValue]

      // Hide custom date range from dropdown menu
      if (isAllowedCustomMonthPicker && optVal) {
        // Check if it's a custom date range format
        const isCustomOption = DATE_RANGE_FORMAT_REGEX.test(optVal)
        if (isCustomOption) {
          return false
        }
      }

      return label.toLowerCase().includes(searchText.toLowerCase())
    })
  }, [optionsWithCustomMonth, searchText, optionLabel, optionValue, isAllowedCustomMonthPicker])

  const selectedValues = useMemo(
    () => new Set(Array.isArray(value) ? value : value === undefined || value === null || value === '' ? [] : [value]),
    [value]
  )

  // Lists long enough to stall the menu are cut to maxVisibleOptions; the selection always stays
  // visible so it can still be cleared, and the search reaches the rest.
  const cappedOptions = useMemo(() => {
    const matches = filteredOptions.filter((opt) => !opt.groupHeader)
    if (!maxVisibleOptions || matches.length <= maxVisibleOptions) return { options: filteredOptions, hidden: 0 }
    let shown = 0
    const kept = filteredOptions.filter((opt) => {
      if (opt.groupHeader) return true
      if (shown >= maxVisibleOptions && !selectedValues.has(opt[optionValue])) return false
      shown += 1
      return true
    })
    return { options: kept, hidden: matches.length - shown }
  }, [filteredOptions, maxVisibleOptions, selectedValues, optionValue])

  // A group header belongs in the list only while an option of its own still follows it.
  const visibleOptions = useMemo(
    () =>
      cappedOptions.options.filter((opt, idx) => {
        if (!opt.groupHeader) return true
        const next = cappedOptions.options[idx + 1]
        return Boolean(next) && !next.groupHeader
      }),
    [cappedOptions]
  )

  const hasOptions = visibleOptions.some((opt) => !opt.groupHeader)

  // One wrapper keeps the label a single flex item; bare parts would be spread apart by the menu item.
  const highlightMatch = (text: string): ReactNode => {
    const term = searchText.trim()
    if (!term) return text
    return (
      <span>
        {text.split(new RegExp(`(${escapeRegExp(term)})`, 'gi')).map((part, idx) =>
          part.toLowerCase() === term.toLowerCase() ? (
            <span key={idx} className="search-match">
              {part}
            </span>
          ) : (
            part
          )
        )}
      </span>
    )
  }

  const isOptionSelected = (optionValue: any) => {
    if (Array.isArray(value)) {
      return value.includes(optionValue)
    }
    return value === optionValue
  }

  const handleOnOpenSelect = () => {
    if (selectRef.current) {
      selectRef.current.classList.add('Mui-focused')
      selectRef.current.previousSibling?.classList.add('Mui-focused')
    }
  }

  const handleOnCloseSelect = () => {
    if (selectRef.current) {
      selectRef.current.classList.remove('Mui-focused')
      selectRef.current.previousSibling?.classList.remove('Mui-focused')
    }
    setTimeout(() => {
      setSearchText('')
    }, 0)
  }

  const defaultRenderValueFunc = () => {
    if (Array.isArray(value)) {
      if (!value.length) {
        return placeholder || label
      }
      const selectedLabels = optionsWithCustomMonth
        .filter((opt) => value.includes(opt[optionValue]))
        .map((opt) => opt[optionLabel])
      return selectedLabels.join(', ')
    } else if (value) {
      const selectedOption = optionsWithCustomMonth.find((opt) => opt[optionValue] === value)
      return selectedOption?.[optionLabel] || placeholder || label
    }
    return placeholder || label
  }

  return (
    <>
      <FormControl fullWidth>
        <StyledInputLabel id={id}>{label}</StyledInputLabel>
        <StyledSelect
          id={id}
          labelId={id}
          label={label}
          value={value}
          ref={selectRef}
          MenuProps={MenuProps}
          onOpen={handleOnOpenSelect}
          onClose={handleOnCloseSelect}
          IconComponent={KeyboardArrowDownIcon}
          displayEmpty={Boolean(placeholder) && !label}
          renderValue={defaultRenderValueFunc}
          {...props}
        >
          {searchable && (
            <StyledListSubheader>
              <StyledTextField
                autoFocus
                autoComplete="off"
                fullWidth
                size="small"
                placeholder="Search"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
                onChange={(e) => setSearchText(e.target.value)}
                // MenuList's type-ahead would otherwise move focus to a matching option and swallow the keystroke.
                onKeyDown={(ev) => ev.stopPropagation()}
              />
            </StyledListSubheader>
          )}
          {hasOptions ? (
            visibleOptions.map((option, idx) => {
              const itemLabel = String(option[optionLabel])
              const itemValue = option[optionValue]

              if (option.groupHeader) {
                return (
                  <StyledListSubheader key={idx} className="group-header">
                    {itemLabel}
                  </StyledListSubheader>
                )
              }

              // Show popover for items with 'custom' in label
              if (isAllowedCustomMonthPicker && itemLabel.toLowerCase() === 'custom') {
                return (
                  <HoveredPopover
                    key={idx}
                    closeDelay={200}
                    onMouseLeavePopover={removeHoverState}
                    trigger={({ onMouseEnter, onMouseLeave }) => (
                      <StyledMenuItem key={idx} className={hoveredOption === itemLabel ? 'hovered' : ''}>
                        <Stack
                          width="100%"
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          onMouseEnter={(event) => {
                            setHoveredOption(itemLabel)
                            onMouseEnter(event)
                          }}
                          onMouseLeave={onMouseLeave}
                        >
                          {itemLabel}
                          <ArrowForwardIosIcon />
                        </Stack>
                      </StyledMenuItem>
                    )}
                  >
                    {({ onClose }) => (
                      <MonthRangePickerV2
                        value={value}
                        onMonthSelect={(range) => {
                          if (props.onChange) {
                            const rangeValue = `${range.dateFrom} to ${range.dateTo}`
                            const event = {
                              target: {
                                id,
                                value: rangeValue,
                                name: props.name || id
                              }
                            } as any
                            props.onChange(event, rangeValue as any)
                          }
                          onClose()
                        }}
                        onCancel={onClose}
                      />
                    )}
                  </HoveredPopover>
                )
              }
              if (optionControl === 'radio') {
                return (
                  <StyledMenuItem
                    key={idx}
                    value={itemValue}
                    disabled={option.disabled}
                    onMouseEnter={removeHoverState}
                    className={isOptionSelected(itemValue) ? 'radio-option selected' : 'radio-option'}
                  >
                    {isOptionSelected(itemValue) ? <RadioCheckedIcon /> : <RadioUncheckedIcon />}
                    {highlightMatch(itemLabel)}
                  </StyledMenuItem>
                )
              }

              return (
                <StyledMenuItem key={idx} value={itemValue} disabled={option.disabled} onMouseEnter={removeHoverState}>
                  {highlightMatch(itemLabel)}
                  {isOptionSelected(itemValue) && <CheckIcon />}
                </StyledMenuItem>
              )
            })
          ) : (
            <StyledMenuItem className="not-found">
              {searchText.trim() ? NO_SEARCH_RESULTS_MESSAGE : emptyMessage}
            </StyledMenuItem>
          )}
          {cappedOptions.hidden > 0 && (
            <StyledListSubheader className="group-header">
              {`${cappedOptions.hidden.toLocaleString('en-US')} more — search to narrow the list`}
            </StyledListSubheader>
          )}
          {listAction && (
            <StyledListSubheader className="list-action" onClick={listAction.onClick}>
              {listAction.label}
            </StyledListSubheader>
          )}
        </StyledSelect>
        {error && helperText && (
          <FormHelperText error={error} sx={{ marginLeft: 0, color: `${theme.colors.errorLight} !important` }}>
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    </>
  )
}

export default memo(SelectV2)
